import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { publicAPI } from '../../api';
import { Loading, Modal } from '../../components';
import './Analyse.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Set Mapbox token from environment
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

const Analyse = () => {
  // Date filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // State & City filter
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [stateStats, setStateStats] = useState({ pass: 0, fail: 0 });

  // Location Type filter
  const [locationTypes, setLocationTypes] = useState([]);
  const [selectedLocationType, setSelectedLocationType] = useState('');
  const [locationTypeStats, setLocationTypeStats] = useState({ pass: 0, fail: 0 });

  // Parameter Distribution
  const [parameterStats, setParameterStats] = useState([]);

  // Failures Analysis
  const [failureThreshold, setFailureThreshold] = useState(3);
  const [failureStats, setFailureStats] = useState([]);

  // Parameter detail modal
  const [showParamModal, setShowParamModal] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [paramSamples, setParamSamples] = useState([]);
  const [paramViewMode, setParamViewMode] = useState('list');
  const [loadingSamples, setLoadingSamples] = useState(false);

  // Map View state
  const [mapSamples, setMapSamples] = useState([]);
  const [selectedMapSample, setSelectedMapSample] = useState(null);
  const [showSampleModal, setShowSampleModal] = useState(false);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);

  const [loading, setLoading] = useState(true);

  // Key parameters to display
  const KEY_PARAMETERS = [
    { code: 'PH', name: 'pH', displayName: 'pH' },
    { code: 'TURBIDITY', name: 'Turbidity', displayName: 'Turbidity' },
    { code: 'TRUE_COLOUR', name: 'True Colour', displayName: 'True Colour' },
    { code: 'ODOUR', name: 'Odour', displayName: 'Odour' },
    { code: 'TDS', name: 'Total Dissolved Solids', displayName: 'TDS' },
    { code: 'FREE_CHLORINE', name: 'Free Residual Chlorine', displayName: 'Free Chlorine' }
  ];

  // Fetch initial filter options
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [statesRes, locationTypesRes] = await Promise.all([
          publicAPI.getStates(),
          publicAPI.getMasterLocationTypes()
        ]);

        const statesData = statesRes.data.data || [];
        setStates(statesData);

        const rawLocationTypes = locationTypesRes.data.data || [];
        const locationTypeNames = rawLocationTypes.map(type =>
          typeof type === 'string' ? type : type.name
        );
        setLocationTypes(locationTypeNames);

        if (locationTypeNames.length > 0) {
          setSelectedLocationType(locationTypeNames[0]);
        }
      } catch (err) {
        console.error('Error fetching filters:', err);
      }
    };
    fetchFilters();
  }, []);

  // Fetch districts when state changes
  useEffect(() => {
    if (!selectedState) {
      setDistricts([]);
      setSelectedDistrict('');
      return;
    }
    const state = states.find(s => s.name === selectedState);
    if (state?.districts) {
      const districtNames = state.districts.map(d =>
        typeof d === 'string' ? d : d.name
      );
      setDistricts(districtNames);
    } else {
      setDistricts([]);
    }
    setSelectedDistrict('');
  }, [selectedState, states]);

  // Fetch all statistics and map data
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const [statsRes, paramStatsRes, mapDataRes] = await Promise.all([
        publicAPI.getStats(params),
        publicAPI.getParameterStats(params),
        publicAPI.getMapData(params)
      ]);

      // Process parameter stats - map _id to code
      const rawParams = paramStatsRes.data.data || [];
      const processedParams = rawParams.map(p => ({
        ...p,
        code: p._id, // API returns _id as the parameter code
        name: p.name || p._id
      }));
      setParameterStats(processedParams);

      if (statsRes.data.data?.byFailedParams) {
        setFailureStats(statsRes.data.data.byFailedParams);
      }

      // Set map samples
      setMapSamples(mapDataRes.data.data || []);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch state-based stats
  const fetchStateStats = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selectedState) params.state = selectedState;
      if (selectedDistrict) params.district = selectedDistrict;

      const response = await publicAPI.getDistrictStats(params);
      const data = response.data.data || [];

      const totalPass = data.reduce((sum, d) => sum + (d.pass || 0), 0);
      const totalFail = data.reduce((sum, d) => sum + (d.fail || 0), 0);
      setStateStats({ pass: totalPass, fail: totalFail });
    } catch (err) {
      console.error('Error fetching state stats:', err);
      setStateStats({ pass: 0, fail: 0 });
    }
  }, [selectedState, selectedDistrict, fromDate, toDate]);

  // Fetch location type stats
  const fetchLocationTypeStats = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selectedLocationType) params.locationType = selectedLocationType;

      const response = await publicAPI.getLocationTypeStats(params);
      const data = response.data.data || {};
      setLocationTypeStats({
        pass: data.pass || 0,
        fail: data.fail || 0
      });
    } catch (err) {
      console.error('Error fetching location type stats:', err);
      setLocationTypeStats({ pass: 0, fail: 0 });
    }
  }, [selectedLocationType, fromDate, toDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchStateStats();
  }, [fetchStateStats]);

  useEffect(() => {
    fetchLocationTypeStats();
  }, [fetchLocationTypeStats]);

  // Initialize Mapbox map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;
    if (!mapboxgl.accessToken) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [78.9629, 20.5937], // Center of India
      zoom: 4
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [loading]);

  // Update map markers when samples change
  useEffect(() => {
    if (!map.current || mapSamples.length === 0) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add new markers
    const bounds = new mapboxgl.LngLatBounds();
    let hasValidCoords = false;

    mapSamples.forEach(sample => {
      if (!sample.coordinates?.longitude || !sample.coordinates?.latitude) return;

      hasValidCoords = true;
      const isSuccess = sample.overallStatus === 'ACCEPTABLE';

      // Create marker element
      const el = document.createElement('div');
      el.className = `map-marker ${isSuccess ? 'success' : 'failed'}`;
      el.innerHTML = `<div class="marker-bubble"></div>`;

      // Create popup for hover - show parameters
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 25
      }).setHTML(`
        <div class="marker-popup">
          <strong>${sample.sampleId}</strong>
          <p>${sample.address || 'No address'}</p>
          <span class="popup-status ${isSuccess ? 'success' : 'failed'}">
            ${sample.overallStatus || 'Unknown'}
          </span>
        </div>
      `);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([sample.coordinates.longitude, sample.coordinates.latitude])
        .addTo(map.current);

      // Add hover events - show params
      el.addEventListener('mouseenter', () => {
        popup.setLngLat([sample.coordinates.longitude, sample.coordinates.latitude])
          .addTo(map.current);
      });
      el.addEventListener('mouseleave', () => popup.remove());

      // Add click event - show sample report
      el.addEventListener('click', () => {
        handleMapSampleClick(sample);
      });

      markersRef.current.push(marker);
      bounds.extend([sample.coordinates.longitude, sample.coordinates.latitude]);
    });

    // Fit map to bounds if we have valid coordinates
    if (hasValidCoords && !bounds.isEmpty()) {
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 10 });
    }
  }, [mapSamples]);

  // Handle map sample click - fetch full sample details and show report
  const handleMapSampleClick = async (sample) => {
    try {
      // Try to get full sample details
      const response = await publicAPI.getSampleById(sample._id || sample.sampleId);
      if (response.data.data) {
        setSelectedMapSample(response.data.data);
      } else {
        setSelectedMapSample(sample);
      }
    } catch (err) {
      // If API fails, use basic sample info
      setSelectedMapSample(sample);
    }
    setShowSampleModal(true);
  };

  // Fetch samples for a specific parameter
  const fetchParameterSamples = async (paramCode) => {
    try {
      setLoadingSamples(true);
      const params = { parameterCode: paramCode, limit: 50 };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const response = await publicAPI.getSamplesByParameter(params);
      setParamSamples(response.data.data?.samples || []);
    } catch (err) {
      console.error('Error fetching parameter samples:', err);
      setParamSamples([]);
    } finally {
      setLoadingSamples(false);
    }
  };

  // Handle parameter bar/card click
  const handleParameterClick = (param) => {
    setSelectedParameter(param);
    setParamViewMode('list');
    setShowParamModal(true);
    fetchParameterSamples(param.code);
  };

  // Get parameter data by code
  const getParamData = (code) => {
    const found = parameterStats.find(p => p.code === code);
    return found || { acceptable: 0, permissible: 0, notAcceptable: 0, total: 0 };
  };

  // Calculate failure count based on threshold
  const getFailureCount = () => {
    return failureStats
      .filter(f => f.failedCount >= failureThreshold)
      .reduce((sum, f) => sum + f.samples, 0);
  };

  // Calculate percentage for semi-circle gauge
  const calculatePercentage = (pass, fail) => {
    const total = pass + fail;
    if (total === 0) return 0;
    return Math.round((fail / total) * 100);
  };

  // Semi-circle gauge component
  const SemiCircleGauge = ({ pass, fail }) => {
    const percentage = calculatePercentage(pass, fail);
    const rotation = (percentage / 100) * 180;
    const total = pass + fail;

    return (
      <div className="gauge-container">
        <div className="gauge-stats">
          <div className="gauge-stat passed">
            <span className="stat-value">{pass}</span>
            <span className="stat-label">PASSED</span>
          </div>
          <div className="gauge-visual">
            <div className="semi-circle">
              <div className="semi-circle-bg"></div>
              <div
                className="semi-circle-fill"
                style={{ transform: `rotate(${rotation}deg)` }}
              ></div>
              <div className="semi-circle-cover"></div>
              <div className="gauge-percentage">{percentage}%</div>
            </div>
            <div className="gauge-total">Total: {total}</div>
          </div>
          <div className="gauge-stat failed">
            <span className="stat-value">{fail}</span>
            <span className="stat-label">FAILED</span>
          </div>
        </div>
      </div>
    );
  };

  // Get state/city label
  const getStateLabel = () => {
    if (selectedDistrict) return `${selectedDistrict}, ${selectedState}`;
    if (selectedState) return `${selectedState} (All Cities)`;
    return 'All Samples';
  };

  // Parameter histogram data
  const parameterChartData = {
    labels: KEY_PARAMETERS.map(p => p.displayName),
    datasets: [
      {
        label: 'Acceptable',
        data: KEY_PARAMETERS.map(p => getParamData(p.code).acceptable || 0),
        backgroundColor: '#10B981',
      },
      {
        label: 'Permissible',
        data: KEY_PARAMETERS.map(p => getParamData(p.code).permissible || 0),
        backgroundColor: '#F59E0B',
      },
      {
        label: 'Not Acceptable',
        data: KEY_PARAMETERS.map(p => getParamData(p.code).notAcceptable || 0),
        backgroundColor: '#EF4444',
      }
    ]
  };

  const parameterChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const keyParam = KEY_PARAMETERS[index];
        const paramData = getParamData(keyParam.code);
        if (keyParam) {
          handleParameterClick({
            code: keyParam.code,
            name: keyParam.name,
            displayName: keyParam.displayName,
            ...paramData
          });
        }
      }
    },
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          afterBody: () => 'Click to view samples (List/Map)'
        }
      }
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true, beginAtZero: true }
    }
  };

  // Parameter distribution donut for modal
  const getParamDonutData = () => {
    if (!selectedParameter) return null;
    return {
      labels: ['Acceptable', 'Permissible', 'Not Acceptable'],
      datasets: [{
        data: [
          selectedParameter.acceptable || 0,
          selectedParameter.permissible || 0,
          selectedParameter.notAcceptable || 0
        ],
        backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
        borderWidth: 0
      }]
    };
  };

  if (loading) {
    return <Loading message="Loading analytics..." />;
  }

  const passedSamples = mapSamples.filter(s => s.overallStatus === 'ACCEPTABLE').length;
  const failedSamples = mapSamples.filter(s => s.overallStatus !== 'ACCEPTABLE').length;

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h1>Sample Analytics Dashboard</h1>
      </div>

      {/* Date Range Filter */}
      <div className="filter-card">
        <div className="date-range-filter">
          <span className="filter-label">Date Range:</span>
          <div className="date-input-group">
            <label>FROM DATE</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="date-input-group">
            <label>TO DATE</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          {(fromDate || toDate) && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => { setFromDate(''); setToDate(''); }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* State & Location Type Cards */}
      <div className="analytics-grid two-cols">
        {/* State & City Based Samples */}
        <div className="analytics-card">
          <h3>State & City Based Samples</h3>
          <div className="filter-dropdowns">
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">All States</option>
              {states.map(state => (
                <option key={state._id} value={state.name}>{state.name}</option>
              ))}
            </select>
            <select
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              disabled={!selectedState}
            >
              <option value="">All Cities</option>
              {districts.map((district, idx) => (
                <option key={idx} value={district}>{district}</option>
              ))}
            </select>
          </div>
          <p className="card-subtitle">Samples - {getStateLabel()}</p>
          <SemiCircleGauge pass={stateStats.pass} fail={stateStats.fail} />
        </div>

        {/* Location Type Based Samples */}
        <div className="analytics-card">
          <h3>Location Type Based Samples</h3>
          <div className="filter-dropdowns single">
            <select
              value={selectedLocationType}
              onChange={(e) => setSelectedLocationType(e.target.value)}
            >
              <option value="">All Location Types</option>
              {locationTypes.map((typeName, idx) => (
                <option key={idx} value={typeName}>{typeName}</option>
              ))}
            </select>
          </div>
          <p className="card-subtitle">Samples - {selectedLocationType || 'All'}</p>
          <SemiCircleGauge pass={locationTypeStats.pass} fail={locationTypeStats.fail} />
        </div>
      </div>

      {/* SECTION 3 - SAMPLES BY PARAMETER DISTRIBUTION */}
      <div className="analytics-card parameter-section">
        <h3>Samples by Parameter Distribution</h3>
        <p className="section-subtitle">KEY PARAMETERS</p>

        {/* Parameter Cards */}
        <div className="key-parameters-grid">
          {KEY_PARAMETERS.map((param) => {
            const data = getParamData(param.code);
            const total = (data.acceptable || 0) + (data.permissible || 0) + (data.notAcceptable || 0);
            return (
              <div
                key={param.code}
                className="parameter-card"
                onClick={() => handleParameterClick({ code: param.code, name: param.name, displayName: param.displayName, ...data })}
              >
                <div className="param-card-header">
                  <span className="param-name">{param.displayName}</span>
                  <span className="param-total">{total} samples</span>
                </div>
                <div className="param-card-bars">
                  <div className="mini-bar acceptable" style={{ width: total > 0 ? `${(data.acceptable / total) * 100}%` : '0%' }}></div>
                  <div className="mini-bar permissible" style={{ width: total > 0 ? `${(data.permissible / total) * 100}%` : '0%' }}></div>
                  <div className="mini-bar not-acceptable" style={{ width: total > 0 ? `${(data.notAcceptable / total) * 100}%` : '0%' }}></div>
                </div>
                <div className="param-card-stats">
                  <span className="stat acceptable">{data.acceptable || 0}</span>
                  <span className="stat permissible">{data.permissible || 0}</span>
                  <span className="stat not-acceptable">{data.notAcceptable || 0}</span>
                </div>
              </div>
            );
          })}
        </div>

        <p className="card-hint chart-hint">Breakdown of tested water quality indicators - Click on a bar or card to view samples</p>

        {/* Histogram Chart */}
        <div className="parameter-chart-container histogram">
          <Bar data={parameterChartData} options={parameterChartOptions} />
        </div>
      </div>

      {/* Failures Analysis */}
      <div className="analytics-card">
        <h3>Failures Analysis</h3>
        <p className="card-hint">Select the minimum number of parameters failed to see total sample impact.</p>
        <div className="failure-tabs">
          {[1, 2, 3, 4, 5].map(num => (
            <button
              key={num}
              className={`failure-tab ${failureThreshold === num ? 'active' : ''}`}
              onClick={() => setFailureThreshold(num)}
            >
              {num}+
            </button>
          ))}
        </div>
        <div className="failure-result">
          <span className="failure-count">{getFailureCount()}</span>
          <span className="failure-label">TOTAL SAMPLES</span>
          <span className="failure-desc">
            failing on <span className="highlight">{failureThreshold} or more</span> parameters
          </span>
        </div>
      </div>

      {/* SECTION 5 - MAP VIEW (AT THE END) */}
      <div className="analytics-card map-section">
        <h3>Sample Locations Map</h3>
        <p className="map-description">
          <span className="legend-inline">
            <span className="legend-dot success"></span> Green: Pass (Acceptable)
          </span>
          <span className="legend-separator">|</span>
          <span className="legend-inline">
            <span className="legend-dot failed"></span> Red: Fail (Not Acceptable / Permissible)
          </span>
        </p>

        <div className="map-stats-bar">
          <span className="total-samples">Total {mapSamples.length} samples</span>
          <div className="map-legend-box">
            <span className="legend-item">
              <span className="legend-dot success"></span> Pass
            </span>
            <span className="legend-item">
              <span className="legend-dot failed"></span> Fail
            </span>
          </div>
        </div>

        <p className="card-hint">Click on a bubble to view sample report. Hover to see parameters.</p>

        {mapboxgl.accessToken ? (
          <div className="map-wrapper">
            <div ref={mapContainer} className="mapbox-container" />
          </div>
        ) : (
          <div className="map-fallback">
            <p>Map requires Mapbox token. Showing sample cards instead.</p>
            <div className="samples-grid">
              {mapSamples.slice(0, 20).map((sample, idx) => (
                <div
                  key={idx}
                  className={`sample-card ${sample.overallStatus === 'ACCEPTABLE' ? 'success' : 'failed'}`}
                  onClick={() => handleMapSampleClick(sample)}
                >
                  <span className="sample-id">{sample.sampleId}</span>
                  <span className="sample-address">{sample.address}</span>
                  <span className={`sample-status ${sample.overallStatus === 'ACCEPTABLE' ? 'success' : 'failed'}`}>
                    {sample.overallStatus}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Parameter Detail Modal - List/Map View */}
      <Modal
        isOpen={showParamModal}
        onClose={() => { setShowParamModal(false); setSelectedParameter(null); setParamSamples([]); }}
        title={selectedParameter ? `${selectedParameter.displayName || selectedParameter.name} Samples` : 'Parameter Samples'}
        size="large"
      >
        {selectedParameter && (
          <div className="param-modal-content">
            <div className="param-summary">
              <div className="param-donut">
                <Doughnut
                  data={getParamDonutData()}
                  options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
                />
              </div>
              <div className="param-stats-summary">
                <div className="stat-item acceptable">
                  <span className="stat-label">Acceptable</span>
                  <span className="stat-value">{selectedParameter.acceptable || 0}</span>
                </div>
                <div className="stat-item permissible">
                  <span className="stat-label">Permissible</span>
                  <span className="stat-value">{selectedParameter.permissible || 0}</span>
                </div>
                <div className="stat-item not-acceptable">
                  <span className="stat-label">Not Acceptable</span>
                  <span className="stat-value">{selectedParameter.notAcceptable || 0}</span>
                </div>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="view-mode-toggle">
              <button
                className={`toggle-btn ${paramViewMode === 'list' ? 'active' : ''}`}
                onClick={() => setParamViewMode('list')}
              >
                List View
              </button>
              <button
                className={`toggle-btn ${paramViewMode === 'map' ? 'active' : ''}`}
                onClick={() => setParamViewMode('map')}
              >
                Map View
              </button>
            </div>

            {loadingSamples ? (
              <Loading message="Loading samples..." />
            ) : paramViewMode === 'list' ? (
              <div className="samples-list">
                {paramSamples.length === 0 ? (
                  <div className="no-samples">No samples found for this parameter</div>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Sample ID</th>
                        <th>Address</th>
                        <th>Value</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paramSamples.map((sample, idx) => (
                        <tr key={idx}>
                          <td>{sample.sampleId}</td>
                          <td className="address-cell" title={sample.address}>{sample.address}</td>
                          <td>{sample.parameter?.value} {sample.parameter?.unit}</td>
                          <td>
                            <span className={`status-badge ${sample.parameter?.status?.toLowerCase().replace('_', '-')}`}>
                              {sample.parameter?.status}
                            </span>
                          </td>
                          <td>{new Date(sample.collectedAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              <div className="param-map-view">
                {paramSamples.filter(s => s.location?.coordinates).length === 0 ? (
                  <div className="no-samples">No samples with location data</div>
                ) : (
                  <div className="param-samples-map">
                    {paramSamples.filter(s => s.location?.coordinates).map((sample, idx) => (
                      <div
                        key={idx}
                        className={`map-sample-item ${sample.parameter?.status?.toLowerCase().replace('_', '-')}`}
                      >
                        <div className="sample-marker"></div>
                        <div className="sample-info">
                          <strong>{sample.sampleId}</strong>
                          <span>{sample.address}</span>
                          <span className="param-value">
                            {sample.parameter?.value} {sample.parameter?.unit} - {sample.parameter?.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Sample Detail Modal (from Map Click) */}
      <Modal
        isOpen={showSampleModal}
        onClose={() => { setShowSampleModal(false); setSelectedMapSample(null); }}
        title={selectedMapSample ? `Sample Report - ${selectedMapSample.sampleId}` : 'Sample Report'}
        size="large"
      >
        {selectedMapSample && (
          <div className="sample-report-content">
            <div className="report-header">
              <div className={`status-indicator ${selectedMapSample.overallStatus === 'ACCEPTABLE' ? 'success' : 'failed'}`}>
                {selectedMapSample.overallStatus || 'Unknown'}
              </div>
            </div>

            <div className="report-details">
              <div className="detail-row">
                <span className="detail-label">Sample ID:</span>
                <span className="detail-value">{selectedMapSample.sampleId}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Address:</span>
                <span className="detail-value">{selectedMapSample.address}</span>
              </div>
              {selectedMapSample.coordinates && (
                <div className="detail-row">
                  <span className="detail-label">Coordinates:</span>
                  <span className="detail-value">
                    {selectedMapSample.coordinates.latitude}, {selectedMapSample.coordinates.longitude}
                  </span>
                </div>
              )}
            </div>

            {selectedMapSample.parameters && selectedMapSample.parameters.length > 0 && (
              <div className="report-parameters">
                <h4>Parameters</h4>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedMapSample.parameters.map((param, idx) => (
                      <tr key={idx}>
                        <td>{param.name || param.code}</td>
                        <td>{param.value} {param.unit}</td>
                        <td>
                          <span className={`status-badge ${param.status?.toLowerCase().replace('_', '-')}`}>
                            {param.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="report-actions">
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (selectedMapSample._id) {
                    publicAPI.downloadPDF(selectedMapSample._id, selectedMapSample.sampleId);
                  }
                }}
              >
                Download PDF Report
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Analyse;
